import React, { FunctionComponent } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { AInfoSection, AInfoSectionItem, Skeleton } from '@atiautomacao/ati-ui-library';
import { useAppSelector } from '../../../../../../Config/Hooks';
import { RootState } from '../../../../../../Config/Store';
import {
    FindInverterInstantInfoByEquipmentId,
    InstantInfoValue,
} from '../../InverterReactQuery';
import { buildInstantInfoRows } from './instantInfoModel';

export interface InstantInfoSectionProps {
    equipmentId: number;
    powerStationId: number | undefined;
}

interface InstantInfoCardConfig {
    key: string;
    title: string;
    values: InstantInfoValue[] | undefined;
}

const ERROR_MESSAGE = 'Não foi possível carregar as informações instantâneas. Tente novamente.';

const InstantInfoCardFeedback: FunctionComponent<{ message: string }> = ({ message }) => (
    <Box role="alert" sx={{ alignItems: 'center', display: 'flex', minHeight: 64, padding: '16px', width: '100%' }}>
        <Typography variant="body2" color="error">
            {message}
        </Typography>
    </Box>
);

const InstantInfoCard: FunctionComponent<{ config: InstantInfoCardConfig; isError: boolean }> = ({
    config,
    isError,
}) => {
    const rows = buildInstantInfoRows(config.values);
    const items: AInfoSectionItem[] = rows.map((row) => ({
        key: row.key,
        label: row.name,
        value: row.valueText,
    }));

    return (
        <AInfoSection
            title={config.title}
            items={items}
            ariaLabel={`dashboard-station-inverter-instant-info-${config.key}`}
            content={isError ? <InstantInfoCardFeedback message={ERROR_MESSAGE} /> : undefined}
        />
    );
};

const InstantInfoSection: FunctionComponent<InstantInfoSectionProps> = ({ equipmentId, powerStationId }) => {
    const userConfig = useAppSelector((state: RootState) => state.userConfigurarion);

    const instantInfoQuery = FindInverterInstantInfoByEquipmentId(
        equipmentId,
        powerStationId,
        userConfig.mainUpdateInterval
    );

    const isLoading = equipmentId > 0 && !!powerStationId && instantInfoQuery.isLoading;
    const isError = instantInfoQuery.isError;
    const data = instantInfoQuery.data?.data;

    const cards: InstantInfoCardConfig[] = [
        { key: 'dc-side', title: 'Lado DC', values: data?.dcSideDataList },
        { key: 'ac-voltage', title: 'Tensão AC', values: data?.acVoltageDataList },
        { key: 'ac-current', title: 'Corrente AC', values: data?.acCurrentDataList },
        { key: 'operating-data', title: 'Dados de Funcionamento', values: data?.operatingDataList },
    ];

    return (
        <Grid container spacing={2}>
            {cards.map((card) => (
                <Grid item xs={12} sm={6} md={6} lg={3} key={card.key}>
                    {isLoading ? (
                        <Skeleton animation="wave" height={105} variant="rounded" width="100%" />
                    ) : (
                        <InstantInfoCard config={card} isError={isError} />
                    )}
                </Grid>
            ))}
        </Grid>
    );
};

export default InstantInfoSection;
